import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProjectApi } from "~/functions/api/project";
import { loadIntendedUrlFromStorage, setCurrentProject, setIntendedUrl } from "~/slices/auth";
import type { AppDispatch, RootState } from "~/store";
import { Loading } from "../ui/loading";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresProject?: boolean;
  requiresOrganization?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresProject = false,
  requiresOrganization = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const paramProjectId = params["projectId"];
  const paramOrganizationId = params["organizationId"];
  const [isChecking, setIsChecking] = useState(true);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const loadedProjectId = useRef<string | null>(null);
  const hasCheckedAuth = useRef(false);
  // 直近でフェッチ失敗した projectId を記録し、同一IDでの即時再試行を抑止
  const failedProjectIdRef = useRef<string | null>(null);
  const hasShownFailedToastRef = useRef<boolean>(false);
  // 即時の重複フェッチ（StrictMode等）を抑止
  const isFetchingProjectRef = useRef<boolean>(false);
  // 同一パスでの再評価ループ抑止（成功・失敗いずれも一度だけ評価）
  const evaluatedOnceRef = useRef<boolean>(false);

  const { currentUser, currentProject, organization, status } = useSelector(
    (state: RootState) => state.auth,
  );

  // 現在のパスを取得
  const currentPath = location.pathname + location.search + location.hash;

  useEffect(() => {
    dispatch(loadIntendedUrlFromStorage());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const checkAuthentication = async () => {
      // 開発環境でのみデバッグログを表示
      if (import.meta.env.MODE === "development") {
        console.log("ProtectedRoute check:", {
          currentPath,
          hasUser: !!currentUser?.id,
          hasOrganization: !!organization?.id,
          hasProject: !!currentProject?.id,
          requiresProject,
          requiresOrganization,
          status,
          isLoadingProject,
          loadedProjectId: loadedProjectId.current,
          paramsProjectId: paramProjectId,
        });
      }

      // ログイン関連のページは認証チェックをスキップ
      if (currentPath === "/signin" || currentPath === "/signup") {
        setIsChecking(false);
        hasCheckedAuth.current = true;
        return;
      }

      // 認証状態をチェック
      if (status === "loading") {
        return; // まだロード中
      }

      // 既に認証チェック済みで、ユーザー情報がない場合はスキップ
      if (hasCheckedAuth.current && !currentUser?.id) {
        return;
      }

      // ユーザーが認証されていない場合
      if (!currentUser || !currentUser.id || !currentUser.organization_id) {
        hasCheckedAuth.current = true;
        if (!cancelled) dispatch(setIntendedUrl(currentPath));
        if (!cancelled) navigate("/signin", { replace: true });
        return;
      }

      // 組織情報が必要だが存在しない場合
      if (requiresOrganization && (!organization || !organization.id)) {
        hasCheckedAuth.current = true;
        if (!cancelled) dispatch(setIntendedUrl(currentPath));
        if (!cancelled) toast.error(TOAST_MESSAGES.AUTH.FETCH_ORGANIZATION_FAILED);
        if (!cancelled) navigate("/signin", { replace: true });
        return;
      }

      // プロジェクト情報が必要だが存在しない場合、または異なるプロジェクトの場合
      if (requiresProject) {
        const projectId = paramProjectId;
        const organizationId = paramOrganizationId;

        // このルートでは既に評価済みなら再実行しない
        if (evaluatedOnceRef.current) {
          return;
        }
        // 1ルートにつき評価は1度のみ（再レンダーやStrictModeの二重実行対策）
        evaluatedOnceRef.current = true;

        // URLのorganizationIdが現在のユーザーの所属と異なる場合は即時退避
        if (
          organizationId &&
          currentUser?.organization_id &&
          organizationId !== currentUser.organization_id
        ) {
          toast.error(TOAST_MESSAGES.ROUTING.ORGANIZATION_MISMATCH);
          evaluatedOnceRef.current = true;
          navigate(`/${currentUser.organization_id}/home`, { replace: true });
          return;
        }

        // 既に正しいプロジェクトが読み込まれている場合はスキップ
        if (currentProject && currentProject.id === projectId) {
          // 既に正しいプロジェクトがロード済み。前回のローディング状態が残っていた場合に備えて明示的に解除
          setIsLoadingProject(false);
          setIsChecking(false);
          hasCheckedAuth.current = true;
          return;
        }

        // 直前に失敗した同一プロジェクトIDは即時再試行しない
        if (failedProjectIdRef.current && failedProjectIdRef.current === projectId) {
          // 同一IDで直前に失敗済み。確実にホームへ退避させる
          const orgId = organizationId || currentUser.organization_id;
          if (orgId) {
            if (!hasShownFailedToastRef.current) {
              toast.error(TOAST_MESSAGES.ROUTING.PROJECT_NOT_FOUND);
              hasShownFailedToastRef.current = true;
            }
            evaluatedOnceRef.current = true;
            navigate(`/${orgId}/home`, { replace: true });
          }
          return;
        }

        // 現在読み込み中のプロジェクトと同じ場合はスキップ（重複防止）
        if (isLoadingProject && loadedProjectId.current === projectId) {
          return;
        }

        if (projectId && organizationId && !isLoadingProject && !isFetchingProjectRef.current) {
          setIsLoadingProject(true);
          isFetchingProjectRef.current = true;
          loadedProjectId.current = projectId;

          try {
            const projectApi = new ProjectApi();
            // まずメンバーシップ確認（常に先行チェック）
            const list = await projectApi.getAllProjects(organizationId);
            const belongs =
              Array.isArray(list?.projects) &&
              list!.projects.some((p: any) => p?.project?.id === projectId);
            if (!belongs) {
              toast.error(TOAST_MESSAGES.ROUTING.PROJECT_NOT_FOUND);
              hasShownFailedToastRef.current = true;
              failedProjectIdRef.current = projectId;
              const orgId = organizationId || currentUser.organization_id;
              if (orgId) {
                evaluatedOnceRef.current = true;
                navigate(`/${orgId}/home`, { replace: true });
              }
              return;
            }

            const project = await projectApi.get(organizationId, projectId);

            if (project) {
              // 直後に別プロジェクトへ遷移された場合に備え、cancelledチェック
              if (!cancelled) {
                dispatch(setCurrentProject(project));
                setIsChecking(false);
                hasCheckedAuth.current = true;
                failedProjectIdRef.current = null; // 成功したのでリセット
                evaluatedOnceRef.current = true;
              }
            } else {
              if (!cancelled) {
                toast.error(TOAST_MESSAGES.ROUTING.PROJECT_NOT_FOUND);
                hasShownFailedToastRef.current = true;
                failedProjectIdRef.current = projectId; // 同一IDでの無限再試行防止
                const orgId = organizationId || currentUser.organization_id;
                if (orgId) {
                  evaluatedOnceRef.current = true;
                  navigate(`/${orgId}/home`, { replace: true });
                }
              }
            }
          } catch (_error) {
            if (!cancelled) {
              toast.error(TOAST_MESSAGES.ROUTING.PROJECT_LOAD_FAILED);
              hasShownFailedToastRef.current = true;
              failedProjectIdRef.current = projectId; // 同一IDでの無限再試行防止
              const orgId = organizationId || currentUser.organization_id;
              if (orgId) {
                evaluatedOnceRef.current = true;
                navigate(`/${orgId}/home`, { replace: true });
              }
            }
          } finally {
            // ローディング状態は必ず解除（ルートが変わっても解除する）
            setIsLoadingProject(false);
            isFetchingProjectRef.current = false;
            loadedProjectId.current = null;
          }
          return;
        } else if (!projectId) {
          // プロジェクトIDがない場合はホームページにリダイレクト
          if (currentUser.organization_id) {
            if (!cancelled) {
              evaluatedOnceRef.current = true;
              navigate(`/${currentUser.organization_id}/home`, { replace: true });
            }
          }
          return;
        }
      }

      if (!cancelled) setIsChecking(false);
      hasCheckedAuth.current = true;
    };
    checkAuthentication();
    return () => {
      cancelled = true;
    };
  }, [
    status,
    currentPath,
    requiresProject,
    requiresOrganization,
    paramProjectId,
    paramOrganizationId,
    currentUser,
    organization,
    currentProject,
    dispatch,
    navigate,
    // Note: isLoadingProject was intentionally removed from dependencies to fix race condition.
    // When isLoadingProject changes during API fetch, it was triggering effect cleanup
    // which set cancelled=true, causing setCurrentProject dispatch to be skipped.
  ]);

  // パスが変わったらフラグをリセット
  useEffect(() => {
    hasCheckedAuth.current = false;
    // 別パスに移動したら失敗キャッシュもリセット
    failedProjectIdRef.current = null;
    evaluatedOnceRef.current = false;
  }, []);

  if (isChecking || status === "loading" || isLoadingProject) {
    return <Loading />;
  }

  return <>{children}</>;
};
